/* eslint-disable react-refresh/only-export-components -- vendored shadcn/ui component */
import * as React from 'react';
import * as RechartsPrimitive from 'recharts';

import { cn } from '../../lib/utils';

// ---- Config Type ----

type ChartConfig = Record<
  string,
  {
    label: string;
    color: string;
    icon?: React.ComponentType;
  }
>;

// ---- Context ----

interface ChartContextProps {
  config: ChartConfig;
}

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart(): ChartContextProps {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }
  return context;
}

// ---- Default colors ----

// Re-exported from shared module so existing imports still work
export { CHART_COLORS } from '../../lib/charts/colors';

// ---- ChartContainer ----

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig;
    children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, '')}`;

  // Build CSS variables from config
  const style = React.useMemo(() => {
    const vars: Record<string, string> = {};
    for (const [key, value] of Object.entries(config)) {
      vars[`--color-${key}`] = value.color;
    }
    return vars;
  }, [config]);

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        ref={ref}
        data-chart={chartId}
        className={cn(
          'flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke]]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke]]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-border/30 [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-border/20 [&_.recharts-reference-line_[stroke]]:stroke-border [&_.recharts-sector[stroke]]:stroke-background [&_.recharts-surface]:outline-none',
          className,
        )}
        style={style}
        {...props}
      >
        <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = 'ChartContainer';

// ---- ChartTooltip ----

const ChartTooltip = RechartsPrimitive.Tooltip;

interface TooltipPayloadItem {
  name?: string;
  value?: string | number;
  dataKey?: string | number;
  color?: string;
  payload?: Record<string, unknown>;
}

interface ChartTooltipContentProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  hideLabel?: boolean;
  hideIndicator?: boolean;
  indicator?: 'line' | 'dot' | 'dashed';
  nameKey?: string;
  labelKey?: string;
  className?: string;
}

const ChartTooltipContent = React.forwardRef<HTMLDivElement, ChartTooltipContentProps>(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      hideIndicator = false,
      label,
      labelKey,
      nameKey,
    },
    ref,
  ) => {
    const { config } = useChart();

    if (!active || !payload?.length) {
      return null;
    }

    const tooltipLabel = labelKey ? (config[labelKey]?.label ?? label) : label;

    return (
      <div
        ref={ref}
        className={cn(
          'grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs shadow-xl',
          className,
        )}
      >
        {!hideLabel && tooltipLabel ? <div className="font-medium text-foreground">{tooltipLabel}</div> : null}
        <div className="grid gap-1.5">
          {payload.map((item) => {
            const key = nameKey ? ((item.payload?.[nameKey] as string) ?? item.name) : item.name;
            const itemConfig = config[key ?? ''];
            const indicatorColor = item.color ?? itemConfig?.color ?? 'var(--color-brand)';

            return (
              <div
                key={item.dataKey as string}
                className="flex items-center gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground"
              >
                {!hideIndicator && (
                  <div
                    className={cn(
                      'shrink-0 rounded-[2px] border-[--color-border]',
                      indicator === 'dot' && 'h-2.5 w-2.5 rounded-full',
                      indicator === 'line' && 'h-0.5 w-6',
                      indicator === 'dashed' && 'h-0.5 w-6 border-b-2 border-dashed bg-transparent',
                    )}
                    style={
                      indicator === 'dashed' ? { borderColor: indicatorColor } : { backgroundColor: indicatorColor }
                    }
                  />
                )}
                <div className="flex flex-1 items-baseline justify-between gap-4 leading-none">
                  <div className="text-muted-foreground">{itemConfig?.label ?? key}</div>
                  <div className="font-mono font-medium text-foreground tabular-nums">
                    {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
ChartTooltipContent.displayName = 'ChartTooltipContent';

// ---- ChartLegend ----

const ChartLegend = RechartsPrimitive.Legend;

interface ChartLegendContentProps extends React.ComponentProps<'div'> {
  payload?: {
    value: string;
    type?: string;
    color?: string;
    dataKey?: string;
  }[];
  nameKey?: string;
  hideIcon?: boolean;
}

const ChartLegendContent = React.forwardRef<HTMLDivElement, ChartLegendContentProps>(
  ({ className, payload, nameKey, hideIcon = false }, ref) => {
    const { config } = useChart();

    if (!payload?.length) {
      return null;
    }

    return (
      <div ref={ref} className={cn('flex items-center justify-center gap-4 pt-3', className)}>
        {payload.map((item) => {
          const key = nameKey ?? item.dataKey ?? item.value;
          const itemConfig = config[key ?? ''];

          return (
            <div
              key={item.value}
              className="flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground"
            >
              {!hideIcon && (
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{
                    backgroundColor: item.color ?? itemConfig?.color ?? 'var(--color-brand)',
                  }}
                />
              )}
              <span className="text-muted-foreground">{itemConfig?.label ?? item.value}</span>
            </div>
          );
        })}
      </div>
    );
  },
);
ChartLegendContent.displayName = 'ChartLegendContent';

export { ChartContainer, ChartContext, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent, useChart };
export type { ChartConfig };
